import {
  Component,
  effect,
  ElementRef,
  inject,
  input,
  ViewChild,
} from '@angular/core';
import { TerminalModule, TerminalService } from 'primeng/terminal';
import { Subscription } from 'rxjs';
import { IControllerDetail } from '../../../../../models/controller';
import { TerminalCommand } from '../../../../../services/terminal-command';

@Component({
  selector: 'app-terminal',
  imports: [TerminalModule],
  templateUrl: './terminal.html',
  styleUrl: './terminal.css',
  providers: [TerminalService],
})
export class Terminal {
  readonly terminalCommandService = inject(TerminalCommand);
  controller = input.required<IControllerDetail>();
  @ViewChild('terminal', { read: ElementRef })
  terminalRef!: ElementRef;
  subscription: Subscription;

  listOfAcceptedActions: string[] = [
    'df',
    'ip',
    'uptime',
    'cd',
    'curl',
    'ls',
    'cat',
    'arp',
    'echo',
    'chmod',
    'pidof',
    './ybsa',
    './mbpoll',
  ];
  allowedSubActions = ['Get', 'Set'];
  listOfBlacklistedActions: string[] = ['rm', 'mv', 'nano'];
  commandHistory: string[] = [];
  historyIndex = -1;

  constructor(private terminalService: TerminalService) {
    this.subscription = this.terminalService.commandHandler.subscribe(
      (command) => {
        this.putInHistory(command);
        switch (command) {
          case 'help':
            this.showHelp();
            break;
          case 'clear':
            this.doClear();
            break;
          default:
            this.checkRawCommandInput(command);
            break;
        }
      },
    );

    effect(() => {
      const response = this.terminalCommandService.response();
      if (!response) {
        return;
      }

      if (response.response.info) {
        this.terminalService.sendResponse(response.response.info + '\n\n');
        this.terminalRef.nativeElement.click();
      } else {
        this.terminalService.sendResponse(response.response + '\n\n');
        this.terminalRef.nativeElement.click();
      }
    });
  }

  checkRawCommandInput(rawInput: string) {
    const commandSegments = rawInput
      .split(/&&|\|\||;/)
      .map((cmd) => cmd.trim());

    for (const segment of commandSegments) {
      const cmd = segment.split(/\s+/)[0].toLowerCase();

      if (this.listOfBlacklistedActions.includes(cmd)) {
        this.terminalService.sendResponse(`"${cmd}" is blocked`);
        return;
      }

      if (!this.listOfAcceptedActions.includes(cmd)) {
        const hasAllowedSubAction = this.allowedSubActions.some((action) =>
          commandSegments.some((segment) => segment.includes(action)),
        );

        if (!hasAllowedSubAction) {
          this.terminalService.sendResponse(`"${cmd}" is not allowed`);
          return;
        }
      }
    }
    this.handleCommand(rawInput);
  }

  handleCommand(command: string) {
    this.terminalCommandService.sendCommand(
      this.controller().serialNumber,
      command,
    );

    this.terminalService.sendResponse(
      'Sending command, waiting for response...',
    );
  }

  showHelp() {
    let response = `
      This terminal can be used to do some nice stuff on the box.
      Please note that it's specifically designed to read information. Editing information is not (yet) possible.
      Adding information / scripts can be done with 'echo "<text>" > <filename>' or 'echo "<text>" >> <filename>'.
      You can use the following commands: help, clear, `;

    for (const action of this.listOfAcceptedActions) {
      if (
        action ===
        this.listOfAcceptedActions[this.listOfAcceptedActions.length - 1]
      ) {
        response += `${action}.\n`;
      } else {
        response += `${action}, `;
      }
    }

    response += `Offcourse it's also possible to make requests with YPVCommands, like "GetGridInfo" or "SetDigitalOutputs=O1=0:O2=0" etc.
      With "clear" you can erase all previous text
      With arrow-up and arrow-down you can scroll trough previous inserted commands
      Watch out: you can ONLY use mbpoll with a single request! Otherwise you'll not get an answer!\n\n`;

    this.terminalService.sendResponse(response);
  }

  doClear() {
    const elements = document.querySelectorAll(`.p-terminal-command`);
    for (const element of elements) {
      element.remove();
    }

    const welcomeElement = document.querySelectorAll(
      `.p-terminal-welcome-message`,
    );
    for (const element of welcomeElement) {
      element.remove();
    }
  }

  putInHistory(command: string) {
    this.commandHistory.push(command);
    this.historyIndex = this.commandHistory.length;
  }

  getTerminalWelcomeMessage() {
    let text = `Welcome to box ${this.controller().serialNumber}\n`;
    text += 'The following commands can be used: help, clear, ';

    for (const action of this.listOfAcceptedActions) {
      if (
        action ===
        this.listOfAcceptedActions[this.listOfAcceptedActions.length - 1]
      ) {
        text += `${action}.\n`;
      } else {
        text += `${action}, `;
      }
    }

    text += `It's also possible to do commands like "GetGridInfo" or "SetDigitalOutputs:O1=0:O2=0".
    Please note that it's specifically designed to read information. Editing information is not (yet) possible.
    Adding information / scripts can be done with 'echo "<text>" > <filename>' or 'echo "<text>" >> <filename>'.
    Watch out: you can ONLY use mbpoll with a single request! Otherwise you'll not get an answer!\n\n`;

    return text;
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.commandHistory.length) return;
    const input = this.terminalRef?.nativeElement?.querySelector('input');
    if (!input || document.activeElement !== input) return;

    if (event.key === 'ArrowUp') {
      event.preventDefault();

      this.historyIndex = Math.max(0, this.historyIndex - 1);
      this.setTerminalInput(this.commandHistory[this.historyIndex]);
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      this.historyIndex = Math.min(
        this.commandHistory.length,
        this.historyIndex + 1,
      );

      const value =
        this.historyIndex === this.commandHistory.length
          ? ''
          : this.commandHistory[this.historyIndex];

      this.setTerminalInput(value);
    }
  }

  setTerminalInput(value: string) {
    const input: HTMLInputElement =
      this.terminalRef?.nativeElement.querySelector('input');

    if (input) {
      input.value = value;
      input.dispatchEvent(new Event('input'));
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
